import fontkit from '@pdf-lib/fontkit';
import { PDFDocument, rgb } from 'pdf-lib';

const PAGE_WIDTH = 841.89;
const PAGE_HEIGHT = 595.28;
const MARGIN = 24;
const MIN_HEADER_HEIGHT = 24;
const MIN_ROW_HEIGHT = 22;
const BODY_FONT_SIZE = 6.5;
const HEADER_FONT_SIZE = 6.5;
const LINE_HEIGHT = 8;

const colors = {
    blue: rgb(0.13, 0.52, 0.82),
    header: rgb(0.91, 0.95, 0.98),
    filter: rgb(0.97, 0.97, 0.97),
    alternate: rgb(0.985, 0.985, 0.985),
    border: rgb(0.72, 0.76, 0.79),
    text: rgb(0.13, 0.13, 0.13),
    muted: rgb(0.4, 0.4, 0.4),
};

const getCellValue = (row, column, index) => {
    const value = typeof column.value === 'function' ? column.value(row, index) : row[column.value];
    return String(value === null || value === undefined ? '' : value);
};

const getSecondaryCellValue = (row, column, index) => {
    if (!column.secondaryValue) return '';
    const value = typeof column.secondaryValue === 'function'
        ? column.secondaryValue(row, index) : row[column.secondaryValue];
    return String(value === null || value === undefined ? '' : value);
};

const getColumnWidths = columns => {
    const availableWidth = PAGE_WIDTH - (MARGIN * 2);
    const weights = columns.map(column => column.width || Math.max(1, Math.min(2.4, column.label.length / 9)));
    const totalWeight = weights.reduce((total, weight) => total + weight, 0);
    return weights.map(weight => (availableWidth * weight) / totalWeight);
};

const splitLongWord = (font, word, fontSize, maxWidth) => {
    const parts = [];
    let part = '';
    word.split('').forEach((character) => {
        const candidate = `${part}${character}`;
        if (!part || font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
            part = candidate;
        } else {
            parts.push(part);
            part = character;
        }
    });
    if (part) parts.push(part);
    return parts;
};

const wrapText = (font, value, fontSize, maxWidth) => {
    const words = String(value || '').split(/\s+/).filter(Boolean).reduce((parts, word) => (
        font.widthOfTextAtSize(word, fontSize) <= maxWidth
            ? parts.concat(word)
            : parts.concat(splitLongWord(font, word, fontSize, maxWidth))
    ), []);
    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (!currentLine || font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
            currentLine = candidate;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    });
    if (currentLine) lines.push(currentLine);

    return lines;
};

const drawWrappedText = (page, font, value, x, top, maxWidth, options = {}) => {
    const fontSize = options.fontSize || BODY_FONT_SIZE;
    wrapText(font, value, fontSize, maxWidth).forEach((line, index) => {
        const lineWidth = font.widthOfTextAtSize(line, fontSize);
        const textX = options.textAlign === 'center'
            ? x + Math.max(0, (maxWidth - lineWidth) / 2) : x;
        page.drawText(line, {
            x: textX,
            y: PAGE_HEIGHT - top - fontSize - (index * LINE_HEIGHT),
            size: fontSize,
            font,
            color: options.color || colors.text,
        });
    });
};

const drawExplicitLineText = (page, font, value, x, top, maxWidth, options = {}) => {
    const lines = String(value || '').split(/\r?\n/);
    const lineHeight = options.lineHeight || LINE_HEIGHT;
    lines.forEach((line, index) => {
        const requestedFontSize = options.fontSize || BODY_FONT_SIZE;
        const widthAtSizeOne = font.widthOfTextAtSize(line, 1);
        const fontSize = widthAtSizeOne > 0
            ? Math.min(requestedFontSize, maxWidth / widthAtSizeOne) : requestedFontSize;
        const lineWidth = font.widthOfTextAtSize(line, fontSize);
        const textX = options.textAlign === 'center'
            ? x + Math.max(0, (maxWidth - lineWidth) / 2) : x;
        page.drawText(line, {
            x: textX,
            y: PAGE_HEIGHT - top - fontSize - (index * lineHeight),
            size: fontSize,
            font,
            color: options.color || colors.text,
        });
    });
};

const getWrappedLineCount = (font, value, fontSize, maxWidth) => (
    Math.max(1, wrapText(font, value, fontSize, maxWidth).length)
);

const getHeaderHeight = (fonts, columns, widths) => Math.max(
    MIN_HEADER_HEIGHT,
    ...columns.map((column, index) => (
        (getWrappedLineCount(fonts.bold, column.label, HEADER_FONT_SIZE, widths[index] - 6) * LINE_HEIGHT) + 8
    )),
);

const getCellContentHeight = (fonts, row, column, columnIndex, widths, rowIndex) => {
    const value = getCellValue(row, column, rowIndex);
    const secondaryValue = getSecondaryCellValue(row, column, rowIndex);
    const fontSize = column.fontSize || BODY_FONT_SIZE;
    const lineHeight = column.lineHeight || LINE_HEIGHT;
    const primaryLines = column.explicitLines
        ? Math.max(1, String(value || '').split(/\r?\n/).length)
        : getWrappedLineCount(fonts.regular, value, fontSize, widths[columnIndex] - 6);
    const secondaryLines = secondaryValue
        ? getWrappedLineCount(fonts.regular, secondaryValue, 5.8, widths[columnIndex] - 6) : 0;
    return (primaryLines * lineHeight) + (secondaryLines * LINE_HEIGHT) + 6;
};

const getRowHeights = (fonts, columns, rows, widths, rowOffset = 0) => {
    const heights = rows.map((row, rowIndex) => columns.reduce((maximumHeight, column, columnIndex) => {
        if (column.mergeConsecutive) return maximumHeight;
        return Math.max(
            maximumHeight,
            getCellContentHeight(fonts, row, column, columnIndex, widths, rowOffset + rowIndex),
        );
    }, MIN_ROW_HEIGHT));

    columns.forEach((column, columnIndex) => {
        if (!column.mergeConsecutive) return;
        let rowIndex = 0;
        while (rowIndex < rows.length) {
            const mergeKey = column.mergeBy
                ? column.mergeBy(rows[rowIndex], rowOffset + rowIndex)
                : getCellValue(rows[rowIndex], column, rowOffset + rowIndex);
            let rowSpan = 1;
            while (rowIndex + rowSpan < rows.length) {
                const nextMergeKey = column.mergeBy
                    ? column.mergeBy(rows[rowIndex + rowSpan], rowOffset + rowIndex + rowSpan)
                    : getCellValue(rows[rowIndex + rowSpan], column, rowOffset + rowIndex + rowSpan);
                if (nextMergeKey !== mergeKey) break;
                rowSpan += 1;
            }
            const requiredHeight = getCellContentHeight(
                fonts, rows[rowIndex], column, columnIndex,
                widths, rowOffset + rowIndex
            );
            const availableHeight = heights.slice(rowIndex, rowIndex + rowSpan)
                .reduce((total, height) => total + height, 0);
            if (requiredHeight > availableHeight) {
                const additionalHeight = (requiredHeight - availableHeight) / rowSpan;
                for (let index = rowIndex; index < rowIndex + rowSpan; index += 1) {
                    heights[index] += additionalHeight;
                }
            }
            rowIndex += rowSpan;
        }
    });
    return heights;
};

const drawRectangle = (page, x, top, width, height, color) => {
    page.drawRectangle({
        x,
        y: PAGE_HEIGHT - top - height,
        width,
        height,
        color,
        borderColor: colors.border,
        borderWidth: 0.45,
    });
};

const getFilterRowHeights = (fonts, filters) => {
    const columnWidth = (PAGE_WIDTH - (MARGIN * 2)) / 2;
    const heights = [];
    for (let index = 0; index < filters.length; index += 2) {
        const filtersInRow = filters.slice(index, index + 2);
        heights.push(Math.max(22, ...filtersInRow.map(filter => (
            (getWrappedLineCount(fonts.regular, `${filter.label}:`, 6.5, columnWidth - 12) * LINE_HEIGHT) +
            (getWrappedLineCount(fonts.bold, filter.value, 7, columnWidth - 12) * LINE_HEIGHT) + 6
        ))));
    }
    return heights;
};

const drawFilters = (page, fonts, filters, startTop) => {
    if (!filters.length) return startTop;

    const columnWidth = (PAGE_WIDTH - (MARGIN * 2)) / 2;
    const rowHeights = getFilterRowHeights(fonts, filters);
    const height = rowHeights.reduce((total, rowHeight) => total + rowHeight, 8);
    drawRectangle(page, MARGIN, startTop, PAGE_WIDTH - (MARGIN * 2), height, colors.filter);

    filters.forEach((filter, index) => {
        const column = index % 2;
        const row = Math.floor(index / 2);
        const x = MARGIN + 6 + (column * columnWidth);
        const top = startTop + 5 + rowHeights.slice(0, row)
            .reduce((total, rowHeight) => total + rowHeight, 0);
        const labelLines = getWrappedLineCount(fonts.regular, `${filter.label}:`, 6.5, columnWidth - 12,);
        drawWrappedText(page, fonts.regular, `${filter.label}:`, x, top, columnWidth - 12, {
            fontSize: 6.5, color: colors.muted,
        });
        drawWrappedText(page, fonts.bold, filter.value, x, top + (labelLines * LINE_HEIGHT), columnWidth - 12, {
            fontSize: 7,
        });
    });
    return startTop + height;
};

const drawTable = (
    page, fonts, columns, rows, rowHeights, widths, headerHeight,
    rowOffset, startTop, pageIndex, mergeDisplayPages,
) => {
    let x = MARGIN;

    columns.forEach((column, index) => {
        drawRectangle(page, x, startTop, widths[index], headerHeight, colors.header);
        drawWrappedText(page, fonts.bold, column.label, x + 3, startTop + 4, widths[index] - 6, {
            fontSize: HEADER_FONT_SIZE, textAlign: column.textAlign,
        });
        x += widths[index];
    });

    x = MARGIN;
    columns.forEach((column, columnIndex) => {
        let rowIndex = 0;
        while (rowIndex < rows.length) {
            const row = rows[rowIndex];
            let rowSpan = 1;
            if (column.mergeConsecutive) {
                const mergeKey = column.mergeBy
                    ? column.mergeBy(row, rowOffset + rowIndex)
                    : getCellValue(row, column, rowOffset + rowIndex);
                while (rowIndex + rowSpan < rows.length) {
                    const nextRow = rows[rowIndex + rowSpan];
                    const nextMergeKey = column.mergeBy
                        ? column.mergeBy(nextRow, rowOffset + rowIndex + rowSpan)
                        : getCellValue(nextRow, column, rowOffset + rowIndex + rowSpan);
                    if (nextMergeKey !== mergeKey) break;
                    rowSpan += 1;
                }
            }
            const top = startTop + headerHeight + rowHeights
                .slice(0, rowIndex).reduce((total, height) => total + height, 0);
            const cellHeight = rowHeights
                .slice(rowIndex, rowIndex + rowSpan).reduce((total, height) => total + height, 0);
            const backgroundIndex = row.backgroundGroup === null || row.backgroundGroup === undefined
                ? rowIndex : row.backgroundGroup;
            const alternateColor = row.strongAlternate ? rgb(0.94, 0.94, 0.94) : colors.alternate;
            drawRectangle(
                page, x, top, widths[columnIndex], cellHeight,
                backgroundIndex % 2 === 0 ? rgb(1, 1, 1) : alternateColor,
            );
            const mergeKey = column.mergeBy
                ? column.mergeBy(row, rowOffset + rowIndex) : null;
            const displayMergedValue = !column.mergeDisplayOnce ||
                mergeDisplayPages[columnIndex][mergeKey] === pageIndex;
            if (displayMergedValue) {
                const cellValue = getCellValue(row, column, rowOffset + rowIndex);
                const secondaryValue = getSecondaryCellValue(row, column, rowOffset + rowIndex);
                const explicitLineCount = column.explicitLines
                    ? String(cellValue || '').split(/\r?\n/).length : 0;
                const explicitContentHeight = column.explicitLines
                    ? (column.fontSize || BODY_FONT_SIZE) +
                        ((explicitLineCount - 1) * (column.lineHeight || LINE_HEIGHT))
                    : 0;
                const contentTop = column.explicitLines
                    ? top + Math.max(2, (cellHeight - explicitContentHeight) / 2)
                    : (() => {
                        const primaryLineCount = getWrappedLineCount(
                            fonts.regular, cellValue, column.fontSize || BODY_FONT_SIZE,
                            widths[columnIndex] - 6,
                        );
                        const secondaryLineCount = secondaryValue
                            ? getWrappedLineCount(fonts.regular, secondaryValue, 5.8, widths[columnIndex] - 6)
                            : 0;
                        const totalContentHeight = (primaryLineCount * LINE_HEIGHT) +
                            (secondaryLineCount * LINE_HEIGHT);
                        return top + Math.max(3, (cellHeight - totalContentHeight) / 2);
                    })();
                const drawCellText = column.explicitLines ? drawExplicitLineText : drawWrappedText;
                drawCellText(
                    page, fonts.regular, cellValue,
                    x + 3, contentTop, widths[columnIndex] - 6,
                    {
                        textAlign: column.textAlign,
                        fontSize: column.fontSize,
                        lineHeight: column.lineHeight,
                    },
                );
                if (secondaryValue) {
                    const primaryLineCount = column.explicitLines
                        ? explicitLineCount
                        : getWrappedLineCount(
                            fonts.regular, cellValue, column.fontSize || BODY_FONT_SIZE,
                            widths[columnIndex] - 6,
                        );
                    drawWrappedText(
                        page, fonts.regular, secondaryValue, x + 3,
                        contentTop + (primaryLineCount * (column.lineHeight || LINE_HEIGHT)),
                        widths[columnIndex] - 6,
                        {
                            fontSize: 5.8,
                            color: colors.muted,
                            textAlign: column.textAlign,
                        },
                    );
                }
            }
            rowIndex += rowSpan;
        }
        x += widths[columnIndex];
    });
};

const downloadPdf = (bytes, fileName) => {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const loadFont = fileName => fetch(`${process.env.PUBLIC_URL || ''}/fonts/${fileName}`)
    .then((response) => {
        if (!response.ok) throw new Error(`Font ${fileName} could not be loaded.`);
        return response.arrayBuffer();
    });

const printReportAsPdf = async ({
    title, filters, columns, rows, fileName = 'izvjestaj.pdf', titleColor = 'blue',
}) => {
    const pdfDocument = await PDFDocument.create();
    pdfDocument.registerFontkit(fontkit);
    const fontFiles = await Promise.all([loadFont('NotoSans-Regular.ttf'), loadFont('NotoSans-Bold.ttf')]);
    const fonts = {
        regular: await pdfDocument.embedFont(fontFiles[0], { subset: true }),
        bold: await pdfDocument.embedFont(fontFiles[1], { subset: true }),
    };

    const filterHeight = filters.length
        ? getFilterRowHeights(fonts, filters).reduce((total, rowHeight) => total + rowHeight, 8)
        : 0;
    const firstPageTableTop = 43 + filterHeight + 20;
    const widths = getColumnWidths(columns);
    const headerHeight = getHeaderHeight(fonts, columns, widths);
    const allRowHeights = getRowHeights(fonts, columns, rows, widths);
    const pageRows = [];
    const pageRowHeights = [];
    let currentRows = [];
    let currentHeights = [];
    let usedHeight = 0;
    rows.forEach((row, index) => {
        const pageIndex = pageRows.length;
        const tableTop = pageIndex === 0 ? firstPageTableTop : 43;
        const availableHeight = PAGE_HEIGHT - tableTop - headerHeight - 24;
        const rowHeight = allRowHeights[index];
        if (currentRows.length && usedHeight + rowHeight > availableHeight) {
            pageRows.push(currentRows);
            pageRowHeights.push(currentHeights);
            currentRows = [];
            currentHeights = [];
            usedHeight = 0;
        }
        currentRows.push(row);
        currentHeights.push(rowHeight);
        usedHeight += rowHeight;
    });
    if (currentRows.length || !pageRows.length) {
        pageRows.push(currentRows);
        pageRowHeights.push(currentHeights);
    }

    const mergeDisplayPages = columns.map(() => ({}));
    columns.forEach((column, columnIndex) => {
        if (!column.mergeDisplayOnce || !column.mergeBy) return;
        const countsByKey = {};
        pageRows.forEach((rowsForPage, pageIndex) => {
            rowsForPage.forEach((row) => {
                const key = column.mergeBy(row);
                if (!countsByKey[key]) countsByKey[key] = {};
                countsByKey[key][pageIndex] = (countsByKey[key][pageIndex] || 0) + 1;
            });
        });
        Object.keys(countsByKey).forEach((key) => {
            mergeDisplayPages[columnIndex][key] = Object.keys(countsByKey[key])
                .map(Number)
                .reduce((selectedPage, candidatePage) => (
                    countsByKey[key][candidatePage] > countsByKey[key][selectedPage]
                        ? candidatePage : selectedPage
                ));
        });
    });

    let rowOffset = 0;
    pageRows.forEach((rowsForPage, pageIndex) => {
        const page = pdfDocument.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        page.drawText(title, {
            x: MARGIN,
            y: PAGE_HEIGHT - 30,
            size: 15,
            font: fonts.bold,
            color: titleColor === 'black' ? colors.text : colors.blue,
        });

        let tableTop = 43;
        if (pageIndex === 0) {
            tableTop = drawFilters(page, fonts, filters, tableTop) + 20;
            page.drawText(`Ukupan broj rezultata: ${rows.length}`, {
                x: MARGIN, y: (PAGE_HEIGHT - tableTop) + 7, size: 7.5, font: fonts.regular, color: colors.muted,
            });
        }
        drawTable(
            page, fonts, columns, rowsForPage, pageRowHeights[pageIndex], widths, headerHeight,
            rowOffset, tableTop, pageIndex, mergeDisplayPages,
        );
        page.drawText(`Stranica ${pageIndex + 1} / ${pageRows.length}`, {
            x: PAGE_WIDTH - MARGIN - 60,
            y: 12,
            size: 6.5,
            font: fonts.regular,
            color: colors.muted,
        });
        rowOffset += rowsForPage.length;
    });

    const pdfBytes = await pdfDocument.save();
    downloadPdf(pdfBytes, fileName);
};

export default printReportAsPdf;



// WEBPACK FOOTER //
// ./src/views/StudentService/StudentsReports/reportPdfPrint.js